import { NextPage } from "next";
import Head from "next/head";
import Link from "next/link";
import clsx from "clsx";
import Section from "@/components/Section2";
import { useAtom } from "jotai";
import {
  footerHeight,
  headerHeight,
  scrollYAfterHeaderAtom,
  scrollYAtom,
  scrollYBeforeFooterAtom,
} from "@/data/hooks/useScrollY";
import React, { HTMLAttributes, useEffect } from "react";
import { kuberHeadTitle } from "@/data/constants";

const PrivacyPolicyPage: NextPage = () => {
  const [scrollY] = useAtom(scrollYAtom);
  const [scrollYAfterHeader, setScrollYAfterHeader] = useAtom(scrollYAfterHeaderAtom);
  const [scrollYBeforeFooter, setScrollYBeforeFooter] = useAtom(scrollYBeforeFooterAtom);

  useEffect(() => {
    const containerHeight = document.getElementById("regulations-context")?.offsetHeight;
    const endScrollYPoint = containerHeight ? containerHeight - footerHeight : 0;
    const middleScrollYPoint = containerHeight ? containerHeight - (containerHeight - headerHeight) : 0;
    setScrollYBeforeFooter(endScrollYPoint);
    setScrollYAfterHeader(middleScrollYPoint);
  }, []);

  return (
    <>
      <Head>
        <title>Правила пользования сайтом | {kuberHeadTitle}</title>
      </Head>

      <Section>
        <div className="max-w-xxl gap-8 pb-4 pt-12 desktop:flex desktop:flex-row desktop:pt-16">
          <div
            className="flex w-full flex-col gap-10 text-textBody tablet:justify-start desktop:basis-2/3"
            id="regulations-context"
          >
            <h2 className="text-header2 desktop:text-header1">Правила пользования сайтом kuber.ru</h2>
            <div className="flex flex-col gap-5 pb-4" id="definitions">
              <h2 className="text-title1">1.&nbsp;Понятия и&nbsp;определения</h2>
              <div className="flex flex-col gap-4">
                <p>
                  <span className="font-semibold">КУБЕР — </span>Общество с&nbsp;ограниченной ответственностью
                  &laquo;КУБЕР&raquo; (ОГРН 1207700248183, ИНН 7722489721).
                </p>
                <p>
                  <span className="font-semibold">Сайт — </span>интернет-ресурс КУБЕР kuber.ru, расположенный
                  на&nbsp;доменном имени kuber.ru, включая любые его поддоменные имена, содержащий совокупность
                  графических и&nbsp;информационных материалов, программ для ЭВМ и&nbsp;баз данных, обеспечивающий
                  доступ пользователям к&nbsp;услугам по&nbsp;перевозке контейнеров автомобильным транспортом,
                  а&nbsp;также к&nbsp;иным любым сопутствующим услугам, связанным с&nbsp;перевозками контейнеров.
                </p>
                <p>
                  <span className="font-semibold">Правила — </span>Правила пользования Сайтом КУБЕР kuber.ru.
                </p>
                <p>
                  <span className="font-semibold">Договор — </span>все договорные правоотношения в&nbsp;рамках
                  действующего законодательства Российской Федерации (далее&nbsp;&mdash;&nbsp;
                  <span className="font-semibold">РФ</span>), одной из&nbsp;сторон которых выступает КУБЕР.
                </p>
                <p>
                  <span className="font-semibold">Юридически значимые действия — </span>действия стороны Договора
                  на&nbsp;Сайте, влекущие юридически значимые последствия в&nbsp;виде установления, изменения и
                  прекращения прав и&nbsp;обязанностей по&nbsp;Договору.
                </p>
                <p>
                  <span className="font-semibold">Контрагент — </span>физическое или юридическое лицо, выступающее
                  противоположной стороной по&nbsp;Договору, осуществляющее лично либо через представителя Юридически
                  значимые действия.
                </p>
                <p>
                  <span className="font-semibold">Заказ — </span>поручение на&nbsp;перевозку контейнера (контейнеров)
                  автомобильным транспортом по&nbsp;Договору.
                </p>
                <p>
                  <span className="font-semibold">Регистрация — </span>процедура установления лица, намеревающегося
                  получить право осуществлять Юридически значимые действия.
                </p>
                <p>
                  <span className="font-semibold">Посетитель — </span>любое физическое лицо, получившее доступ
                  к&nbsp;Сайту через сеть Интернет и&nbsp;не&nbsp;прошедшее Регистрацию.
                </p>
                <p>
                  <span className="font-semibold">Логин — </span>адрес электронной почты, указанный Посетителем при
                  Регистрации.
                </p>
                <p>
                  <span className="font-semibold">Пароль — </span>комбинация цифровых&nbsp;и (или) буквенных&nbsp;и
                  (или) иных символов, связанная с&nbsp;Логином, указанная Посетителем при Регистрации.
                </p>
                <p>
                  <span className="font-semibold">Пользователь — </span>лицо, прошедшее Регистрацию и намеревающееся
                  получить право осуществлять Юридически значимые действия.
                </p>
                <p>
                  <span className="font-semibold">Аутентификация — </span>процедура проверки подлинности Пользователя
                  путем сравнения введенного им&nbsp;логина и&nbsp;пароля с&nbsp;Логином и&nbsp;Паролем, сохраненными
                  в&nbsp;базе данных Сайта при Регистрации.
                </p>
                <p>
                  <span className="font-semibold">Аккаунт — </span>совокупность обязательной и дополнительной
                  информации, которая включает в&nbsp;себя информацию о&nbsp;Пользователе и&nbsp;Контрагенте,
                  необходимую для идентификации Контрагента на&nbsp;Сайте.
                </p>
                <p>
                  <span className="font-semibold">Авторизация — </span>процедура предоставления Пользователю права
                  осуществлять Юридически значимые действия.
                </p>
                <p>
                  <span className="font-semibold">Верификация аккаунта — </span>процедура проверки и подтверждения
                  достоверности информации о&nbsp;Контрагенте, необходимой для размещения на&nbsp;Сайте для совершения
                  им&nbsp;Юридически значимых действий.
                </p>
                <p>
                  <span className="font-semibold">Личный кабинет — </span>сервис на&nbsp;Сайте, активируемый после
                  Авторизации и&nbsp;предназначенный для размещения и&nbsp;корректировки информации в&nbsp;Аккаунте,
                  а&nbsp;также для обмена информацией между сторонами по&nbsp;Договору, с&nbsp;целью получения услуг
                  по&nbsp;перевозке контейнеров автомобильным транспортом.
                </p>
                <p>
                  <span className="font-semibold">Доступ — </span>возможность Контрагента использовать функционал
                  Личного кабинета в&nbsp;пределах, позволяющих совершать Юридически значимых действия.
                </p>
                <p>
                  <span className="font-semibold">Технические средства — </span>любые электронно-технические устройства,
                  эксплуатируемые Пользователем, в&nbsp;том числе, но&nbsp;не&nbsp;ограничиваясь, персональные
                  компьютеры, планшеты, смартфоны, а&nbsp;также иные устройства независимо
                  от&nbsp;их&nbsp;характеристик, операционной системы, используемого программного обеспечения,
                  в&nbsp;том числе, но&nbsp;не&nbsp;ограничиваясь, браузера, антивирусного и&nbsp;иного программного
                  обеспечения.
                </p>
                <p>
                  <span className="font-semibold">Технические ресурсы — </span>технические возможности третьих лиц,
                  эксплуатируемые Пользователем, в&nbsp;том числе, но&nbsp;не&nbsp;ограничиваясь, интернет-провайдеров,
                  и иных лиц, обеспечивающих работоспособность Технических средств.
                </p>
                <p>
                  <span className="font-semibold">Идентификация пользователя — </span>процедура, осуществляемая
                  по&nbsp;решению КУБЕР для подтверждения личности Пользователя, при наличии оснований считать, что
                  данные, указанные им&nbsp;при Регистрации, не&nbsp;являются достоверными, либо от&nbsp;его лица
                  действуют иные лица, незаконно получившие доступ к&nbsp;Аккаунту.
                </p>
                <p>
                  <span className="font-semibold">Содержание сайта — </span>все объекты, размещенные на&nbsp;Сайте,
                  в&nbsp;том числе, но&nbsp;не&nbsp;ограничиваясь, элементы дизайна, текст, графические изображения,
                  иллюстрации, фотографии, видео, скрипты, программы, музыка, звуки.
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-5 pb-4" id="registration">
              <h2 className="text-title1">2. Регистрация и&nbsp;аутентификация</h2>
              <ol className="flex list-inside flex-col gap-4">
                <li className="flex flex-row gap-4">
                  <span>2.1.</span>
                  <span>
                    Для Регистрации Посетителю независимо от&nbsp;его правового статуса необходимо указать&nbsp;и (или)
                    предоставить следующую информацию на&nbsp;Сайте:
                  </span>
                </li>
                <ul className="flex list-disc flex-col gap-3 pb-4 pl-[65px]">
                  <li>
                    <span className="relative left-2">роль Контрагента по&nbsp;Договору;</span>
                  </li>
                  <li>
                    <span className="relative left-2">номер мобильного телефона;</span>
                  </li>
                  <li>
                    <span className="relative left-2">
                      смс-код подтверждения, поступивший на&nbsp;указанный им&nbsp;номер мобильного телефона;
                    </span>
                  </li>
                  <li>
                    <span className="relative left-2">
                      Логин, используемый им&nbsp;для входа в&nbsp;Личный кабинет;
                    </span>
                  </li>
                  <li>
                    <span className="relative left-2">
                      Пароль, используемый им&nbsp;для входа в&nbsp;Личный кабинет.
                    </span>
                  </li>
                </ul>

                <li className="flex flex-row gap-4">
                  <span>2.2.</span>
                  <span>
                    Лицу, намеревающемуся получить право осуществлять Юридически значимые действия, необходимо
                    в&nbsp;полной мере ознакомиться с&nbsp;положениями Правил до&nbsp;момента Регистрации. Регистрация
                    означает полное и&nbsp;безоговорочное принятие лицом, намеревающимся получить право осуществлять
                    Юридически значимые действия, положений настоящих Правил.
                  </span>
                </li>
                <li className="flex flex-row gap-4">
                  <span>2.3.</span>
                  <span>Для Аутентификации Пользователю необходимо ввести Логин и&nbsp;Пароль на&nbsp;Сайте.</span>
                </li>
              </ol>
            </div>
            <div className="flex flex-col gap-5" id="authorization">
              <h2 className="text-title1">3. Авторизация</h2>
              <ol className="flex flex-col gap-4">
                <li className="flex flex-row gap-4">
                  <span>3.1.</span>
                  <span>Для Авторизации Пользователю необходимо указать следующую информацию на&nbsp;Сайте:</span>
                </li>
                <ul className="flex list-disc flex-col gap-3 pb-4 pl-16">
                  <li>
                    <span className="relative left-2">правовой статус Контрагента;</span>
                  </li>
                  <li>
                    <span className="relative left-2">ИНН Контрагента;</span>
                  </li>
                  <li>
                    <span className="relative left-2">КПП Контрагента (при наличии).</span>
                  </li>
                </ul>
              </ol>
            </div>
            <div className="flex flex-col gap-5" id="verification">
              <h2 className="text-title1">4. Верификация аккаунта</h2>
              <ol className="flex flex-col gap-4">
                <li className="flex flex-row gap-3">
                  <span>4.1.</span>
                  <span>
                    Для Верификации аккаунта Контрагент, в&nbsp;зависимости от&nbsp;правового статуса, размещает
                    в&nbsp;Личном кабинете следующие необходимые информацию и&nbsp;документы:
                  </span>
                </li>
                <ol className="flex flex-col gap-[10px] pb-4 pl-10">
                  <li className="font-semibold">
                    4.1.1&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Контрагент&nbsp;&mdash; юридическое лицо:
                  </li>
                  <ul className="flex list-disc flex-col gap-4 pb-4 pl-[60px] tablet:pl-[83px] desktop:pl-[85px]">
                    <li>
                      <span className="relative left-2">почтовый адрес Контрагента;</span>
                    </li>
                    <li>
                      <span className="relative left-2">
                        контактные данные Контрагента (номера телефона, адреса электронной почты);
                      </span>
                    </li>
                    <li>
                      <span className="relative left-2">
                        банковские реквизиты Контрагента (БИК, номер расчетного счета);
                      </span>
                    </li>
                    <li>
                      <span className="relative left-2">
                        фамилия имя отчество физического лица&nbsp;&mdash; представителя Контрагента, выступающего
                        Пользователем;
                      </span>
                    </li>
                    <li>
                      <span className="relative left-2">
                        ИНН физического лица&nbsp;&mdash; представителя Контрагента, выступающего Пользователем;
                      </span>
                    </li>
                    <li>
                      <span className="relative left-2">
                        паспортные данные физического лица&nbsp;&mdash; представителя Контрагента, выступающего
                        Пользователем (серия номер паспорта&nbsp;РФ, дата выдачи, кем выдан, код подразделения);
                      </span>
                    </li>
                    <li>
                      <span className="relative left-2">
                        адрес регистрации физического лица&nbsp;&mdash; представителя Контрагента, выступающего
                        Пользователем (прописка);
                      </span>
                    </li>
                    <li>
                      <span className="relative left-2">
                        скан-копия паспорта физического лица&nbsp;&mdash; представителя Контрагента, выступающего
                        Пользователем (&laquo;разворот&raquo; страниц с&nbsp;фотографией, страница с&nbsp;актуальной
                        пропиской);
                      </span>
                    </li>
                    <li>
                      <span className="relative left-2">
                        контактные данные физического лица&nbsp;&mdash; представителя Контрагента, выступающего
                        Пользователем (номер мобильного телефона, адрес электронной почты);
                      </span>
                    </li>
                    <li>
                      <span className="relative left-2">
                        скан-копия доверенности на&nbsp;физическое лицо&nbsp;&mdash; представителя Контрагента,
                        выступающего Пользователем.
                      </span>
                    </li>
                  </ul>
                  <li className="font-semibold">
                    4.1.2&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Контрагент&nbsp;&mdash; индивидуальный предприниматель:
                  </li>
                  <ul className="flex list-disc flex-col gap-4 pb-4 pl-[60px] tablet:pl-[83px] desktop:pl-[85px]">
                    <li>
                      <span className="relative left-2">
                        паспортные данные Контрагента (серия номер паспорта&nbsp;РФ, дата выдачи, кем выдан, код
                        подразделения);
                      </span>
                    </li>
                    <li>
                      <span className="relative left-2">адрес регистрации Контрагента (прописка);</span>
                    </li>
                    <li>
                      <span className="relative left-2">
                        скан-копия паспорта Контрагента (&laquo;разворот&raquo; страниц с&nbsp;фотографией, страница
                        с&nbsp;актуальной пропиской);
                      </span>
                    </li>
                    <li>
                      <span className="relative left-2">почтовый адрес Контрагента;</span>
                    </li>
                    <li>
                      <span className="relative left-2">
                        контактные данные Контрагента (номера телефона, адреса электронной почты);
                      </span>
                    </li>
                    <li>
                      <span className="relative left-2">
                        банковские реквизиты Контрагента (БИК, номер расчетного счета);
                      </span>
                    </li>
                    <li>
                      <span className="relative left-2">
                        фамилия, имя, отчество физического лица&nbsp;&mdash; представителя Контрагента, выступающего
                        Пользователем, если таковым не&nbsp;выступает индивидуальный предприниматель;
                      </span>
                    </li>
                    <li>
                      <span className="relative left-2">
                        ИНН физического лица&nbsp;&mdash; представителя Контрагента, выступающего Пользователем, если
                        таковым не выступает индивидуальный предприниматель;
                      </span>
                    </li>
                    <li>
                      <span className="relative left-2">
                        паспортные данные физического лица&nbsp;&mdash; представителя Контрагента, выступающего
                        Пользователем (серия номер паспорта&nbsp;РФ, дата выдачи, кем выдан, код подразделения), если
                        таковым не&nbsp;выступает индивидуальный предприниматель;
                      </span>
                    </li>
                    <li>
                      <span className="relative left-2">
                        адрес регистрации физического лица&nbsp;&mdash; представителя Контрагента, выступающего
                        Пользователем (прописка), если таковым не&nbsp;выступает индивидуальный предприниматель;
                      </span>
                    </li>
                    <li>
                      <span className="relative left-2">
                        скан-копия паспорта физического лица&nbsp;&mdash; представителя Контрагента, выступающего
                        Пользователем (&laquo;разворот&raquo; страниц с&nbsp;фотографией, страница с&nbsp;актуальной
                        пропиской), если таковым не&nbsp;выступает индивидуальный предприниматель;
                      </span>
                    </li>
                    <li>
                      <span className="relative left-2">
                        контактные данные физического лица&nbsp;&mdash; представителя Контрагента, выступающего
                        Пользователем (номер мобильного телефона, адрес электронной почты), если таковым
                        не&nbsp;выступает индивидуальный предприниматель;
                      </span>
                    </li>
                    <li>
                      <span className="relative left-2">
                        скан-копия доверенности на&nbsp;физическое лицо&nbsp;&mdash; представителя Контрагента,
                        выступающего Пользователем, если таковым не&nbsp;выступает индивидуальный предприниматель.
                      </span>
                    </li>
                  </ul>
                  <li className="font-semibold">
                    4.1.3&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Контрагент&nbsp;&mdash; самозанятый:
                  </li>
                  <ul className="flex list-disc flex-col gap-4 pb-4 pl-[60px] tablet:pl-[83px] desktop:pl-[85px]">
                    <li>
                      <span className="relative left-2">
                        фамилия, имя, отчество Контрагента, выступающего Пользователем;
                      </span>
                    </li>
                    <li>
                      <span className="relative left-2">
                        паспортные данные Контрагента (серия номер паспорта&nbsp;РФ, дата выдачи, кем выдан, код
                        подразделения), выступающего Пользователем;
                      </span>
                    </li>
                    <li>
                      <span className="relative left-2">
                        адрес регистрации Контрагента (прописка), выступающего Пользователем;
                      </span>
                    </li>
                    <li>
                      <span className="relative left-2">
                        скан-копия паспорта Контрагента (&laquo;разворот&raquo; страниц с&nbsp;фотографией, страница
                        с&nbsp;актуальной пропиской), выступающего Пользователем;
                      </span>
                    </li>
                    <li>
                      <span className="relative left-2">скан-копия СНИЛС Контрагента, выступающего Пользователем;</span>
                    </li>
                    <li>
                      <span className="relative left-2">почтовый адрес Контрагента, выступающего Пользователем;</span>
                    </li>
                    <li>
                      <span className="relative left-2">
                        контактные данные Контрагента (номера телефона, адреса электронной почты), выступающего
                        Пользователем;
                      </span>
                    </li>
                    <li>
                      <span className="relative left-2">
                        банковские реквизиты Контрагента (фамилия, имя, отчество получателя, номер счета получателя, БИК
                        банка получателя), выступающего Пользователем;
                      </span>
                    </li>
                    <li>
                      <span className="relative left-2">
                        регион деятельности Контрагента (регион преимущественного ведения деятельности из&nbsp;профиля
                        самозанятого в&nbsp;личном кабинете приложения &laquo;Мой налог&raquo;), выступающего
                        Пользователем.
                      </span>
                    </li>
                  </ul>
                </ol>
              </ol>
            </div>
            <div className="flex flex-col gap-4 pb-4" id="cabinet">
              <h2 className="text-title1">5. Личный кабинет</h2>
              <ol className="flex flex-col gap-4">
                <li className="flex flex-row gap-3">
                  <span>5.1.</span>
                  <span>
                    Доступ позволяет Контрагенту использовать Личный кабинет для самостоятельного осуществления
                    следующих видов Юридически значимых действий:
                  </span>
                </li>

                <ul className="flex list-disc flex-col gap-3 pb-4 pl-16">
                  <li>
                    <span className="relative left-2">добавление и&nbsp;корректировка информации в&nbsp;Аккаунте;</span>
                  </li>
                  <li>
                    <span className="relative left-2">актуализация информации в&nbsp;Заказе;</span>
                  </li>
                  <li>
                    <span className="relative left-2">актуализация статуса Заказа;</span>
                  </li>
                  <li>
                    <span className="relative left-2">обработка транспортных документов по&nbsp;Заказу;</span>
                  </li>
                  <li>
                    <span className="relative left-2">обработка бухгалтерских документов по&nbsp;Заказу;</span>
                  </li>
                  <li>
                    <span className="relative left-2">обмен информацией по&nbsp;Заказу;</span>
                  </li>
                  <li>
                    <span className="relative left-2">взаиморасчеты по&nbsp;Договору;</span>
                  </li>
                  <li>
                    <span className="relative left-2">прочие действия в&nbsp;рамках Договора.</span>
                  </li>
                  <li>
                    <span className="relative left-2">
                      регион деятельности Контрагента (регион преимущественного ведения деятельности из&nbsp;профиля
                      самозанятого в&nbsp;личном кабинете приложения &laquo;Мой налог&raquo;), выступающего
                      Пользователем.
                    </span>
                  </li>
                </ul>

                <li className="flex flex-row gap-3">
                  <span>5.2.</span>
                  <span>
                    Все действия в&nbsp;Личном кабинете, совершенные любым лицом, прошедшим Аутентификацию,
                    приравниваются к&nbsp;действиям, совершенным Контрагентом.
                  </span>
                </li>
              </ol>
            </div>
            <div className="flex flex-col gap-4" id="agent-rights">
              <h2 className="text-title1">6. Права и&nbsp;обязанности Контрагента</h2>
              <ol className="flex flex-col gap-4">
                <li className="flex flex-row gap-3 font-semibold">
                  <span>6.1.</span>
                  <span>Контрагент вправе:</span>
                </li>
                <ol className="flex flex-col gap-4 pb-4 pl-10">
                  <li className="flex flex-row gap-3">
                    <span>6.1.1.</span>
                    <span>
                      Использовать Сайт на&nbsp;условиях Правил и&nbsp;в&nbsp;пределах всего срока действия Договора.
                      В&nbsp;случае отказа Контрагентом от&nbsp;исполнения Договора либо прекращения срока его действия
                      КУБЕР прекращает Доступ.
                    </span>
                  </li>
                  <li className="flex flex-row gap-3">
                    <span>6.1.2.</span>
                    <span>Осуществлять Юридически значимые действия.</span>
                  </li>
                  <li className="flex flex-row gap-3">
                    <span>6.1.3.</span>
                    <span>
                      По&nbsp;своему усмотрению корректировать информацию в&nbsp;Аккаунте за исключением случаев, когда
                      такая корректировка препятствует исполнению обязательств Контрагента по Договору.
                    </span>
                  </li>
                  <li className="flex flex-row gap-3">
                    <span>6.1.4.</span>
                    <span>
                      Запрашивать у&nbsp;КУБЕР справочную информацию, касающуюся функционирования Личного кабинета
                      и&nbsp;Сайта.
                    </span>
                  </li>
                  <li className="flex flex-row gap-3">
                    <span>6.1.5.</span>
                    <span>Оставлять отзывы и&nbsp;жалобы, касающиеся функционирования Сайта.</span>
                  </li>
                </ol>
                <li className="font-semibold">6.2.&nbsp;&nbsp;&nbsp;Контрагент обязан:</li>
                <ol className="flex flex-col gap-4 pb-4 pl-10">
                  <ListItem section="6.2.1.">
                    <span>Соблюдать положения Правил и&nbsp;действующего законодательства&nbsp;РФ.</span>
                  </ListItem>
                  <ListItem section="6.2.2.">
                    <span>
                      Самостоятельно осуществлять контроль за&nbsp;изменениями&nbsp;и (или) дополнениями, которые КУБЕР
                      вносит в&nbsp;Правила.
                    </span>
                  </ListItem>
                  <ListItem section="6.2.3.">
                    <span>
                      Обеспечить наличие и&nbsp;состояние Технических средств и&nbsp;Технических ресурсов
                      в&nbsp;количестве и&nbsp;качестве, позволяющим использовать функционал Сайта.
                    </span>
                  </ListItem>
                  <ListItem section="6.2.4.">
                    Не&nbsp;создавать Аккаунт с&nbsp;использованием данных, не&nbsp;относящихся к Контрагенту&nbsp;и
                    (или) его представителю.
                  </ListItem>
                  <ListItem section="6.2.5.">
                    Предоставить полную, достоверную, актуальную информацию и документы, необходимые для Верификации
                    аккаунта, а&nbsp;также поддерживать их&nbsp;в&nbsp;актуальном состоянии.
                  </ListItem>
                  <ListItem section="6.2.6.">
                    Размещать в&nbsp;Личном кабинете исключительно полную и&nbsp;достоверную информацию по&nbsp;Заказам.
                  </ListItem>

                  <ListItem section="6.2.7.">
                    Обеспечивать сохранность Логина, Пароля, смс-кода подтверждения, и&nbsp;принимать меры для
                    предотвращения несанкционированного доступа третьих лиц к&nbsp;Аккаунту.
                  </ListItem>
                  <ListItem section="6.2.8.">
                    В&nbsp;случае компрометации Логина&nbsp;и (или) Пароля&nbsp;и (или) смс-кода подтверждения,
                    незамедлительно сообщить об&nbsp;этом в&nbsp;КУБЕР. При этом КУБЕР приостанавливает Доступ на
                    основании заявления Контрагента, переданного способом, позволяющим установить, что такое требование
                    исходит непосредственно от&nbsp;Контрагента.
                  </ListItem>
                  <ListItem section="6.2.9.">
                    Осуществлять резервное копирование важной информации, хранящейся в&nbsp;Аккаунте, во&nbsp;избежание
                    её&nbsp;утраты или компрометации.
                  </ListItem>
                  <ListItem section="6.2.10." doubleDigit={true}>
                    В&nbsp;случае несогласия с&nbsp;новой редакцией Правил прекратить использование Сайта, уведомив
                    об&nbsp;этом КУБЕР через форму обратной связи на&nbsp;Сайте или в&nbsp;письменной форме. Продолжение
                    использования Сайта Контрагентом после внесения изменений&nbsp;и (или) дополнений в&nbsp;Правила
                    означает согласие Контрагента с&nbsp;такими изменениями&nbsp;и (или) дополнениями и&nbsp;принятие
                    им&nbsp;настоящих Правил в&nbsp;новой редакции.
                  </ListItem>
                  <ListItem section="6.2.11." doubleDigit={true}>
                    Не&nbsp;пытаться получить доступ к&nbsp;Логинам и&nbsp;Паролям других Пользователей любым способом,
                    в&nbsp;том числе, но&nbsp;не&nbsp;ограничиваясь, путем обмана, злоупотребления доверием, взлома.
                  </ListItem>
                  <ListItem section="6.2.12." doubleDigit={true}>
                    Не&nbsp;использовать полученные любым способом Логины и&nbsp;Пароли других Пользователей.
                  </ListItem>
                  <ListItem section="6.2.13." doubleDigit={true}>
                    Не&nbsp;разглашать ставшие ему известными персональные данные Пользователей и конфиденциальные
                    данные, относящиеся к&nbsp;Договору.
                  </ListItem>
                  <ListItem section="6.2.14." doubleDigit={true}>
                    Не&nbsp;осуществлять самостоятельно, через любых третьих лиц, а&nbsp;также от&nbsp;имени других
                    Пользователей любые действия, направленные на&nbsp;нарушение нормального функционирования Сайта,
                    противоправный сбор информации о&nbsp;Пользователях&nbsp;и (или) массовую рассылку коммерческой,
                    социальной, политической и&nbsp;иной рекламы и&nbsp;информации Пользователям на&nbsp;Сайте
                    и&nbsp;иным лицам в&nbsp;сети Интернет, не дававшим согласия на&nbsp;получение такой рекламы
                    и&nbsp;информации.
                  </ListItem>
                  <ListItem section="6.2.15." doubleDigit={true}>
                    Возместить любой вред, который КУБЕР понес вследствие нарушения Контрагентом положений Правил
                    и&nbsp;действующего законодательства&nbsp;РФ.
                  </ListItem>
                  <ListItem section="6.2.16." doubleDigit={true}>
                    Не&nbsp;предъявлять в&nbsp;адрес КУБЕР и&nbsp;других Пользователей какие-либо претензии и требования
                    в&nbsp;связи с&nbsp;нарушениями функционирования Сайта, вызванными любыми неправомерными действиями,
                    исходящими от&nbsp;любых установленных и&nbsp;неустановленных лиц и&nbsp;источников, включая,
                    но&nbsp;не ограничиваясь, вредоносное программное обеспечение (вирусы, черви, троянские
                    и&nbsp;шпионские программы), ботнеты (вредоносные боты), DoS- и&nbsp;DDoS-атаки (отказ
                    в&nbsp;обслуживании), форс-мажорными и&nbsp;иными обстоятельствами, независящими от&nbsp;КУБЕР,
                    а&nbsp;также в&nbsp;связи с&nbsp;последствиями таких нарушений.
                  </ListItem>
                  <ListItem section="6.2.17." doubleDigit={true}>
                    В&nbsp;случае несогласия с&nbsp;положениями Правил, или их&nbsp;обновленной редакцией, а также
                    с&nbsp;любой информацией и&nbsp;документами на&nbsp;Сайте, отказаться от&nbsp;использования Сайта,
                    уведомив об этом КУБЕР посредством формы обратной связи на&nbsp;Сайте или в&nbsp;письменной форме.
                  </ListItem>
                </ol>
              </ol>
            </div>
            <div className="flex flex-col gap-4" id="kuber-rights">
              <h2 className="text-title1">7. Права и&nbsp;обязанности КУБЕР</h2>
              <ol className="flex flex-col gap-4">
                <ListItem section="7.1." semibold={true} className="!gap-3">
                  КУБЕР вправе:
                </ListItem>
                <ol className="flex flex-col gap-4 pb-4 pl-10">
                  <ListItem section="7.1.1">
                    Проверять полноту и&nbsp;достоверность всей информации и&nbsp;документов, предоставленных
                    Контрагентом.
                  </ListItem>
                  <ListItem section="7.1.2">
                    Приостановить либо прекратить Доступ в&nbsp;случаях, если Контрагент нарушает положения Правил. При
                    этом датой приостановления (прекращения) Доступа считается дата в уведомлении о&nbsp;приостановлении
                    (прекращении) Доступа, направленном Контрагенту на&nbsp;адрес его электронной почты, указанный при
                    Верификации аккаунта.
                  </ListItem>
                  <ListItem section="7.1.3">
                    Приостановить Доступ при наличии оснований считать, что активность Аккаунта обусловлена
                    несанкционированными действиями третьих лиц.
                  </ListItem>
                  <ListItem section="7.1.4">
                    Производить на&nbsp;Сайте профилактические и&nbsp;иные необходимые работы, направленные
                    на&nbsp;обеспечение его работоспособности, с&nbsp;приостановлением Доступа, заблаговременно уведомив
                    об&nbsp;этом Контрагента путем публикации соответствующего объявления в&nbsp;любом разделе Сайта, с
                    указанием сроков планируемого начала и&nbsp;окончания таких работ.
                  </ListItem>
                  <ListItem section="7.1.5">
                    Возобновить Доступ в&nbsp;случае устранения обстоятельств, послуживших основаниями для его
                    приостановления.
                  </ListItem>
                  <ListItem section="7.1.6">
                    Совершать Юридически значимые действия с&nbsp;использованием усиленной квалифицированной электронной
                    подписи, а&nbsp;также требовать её&nbsp;использование от&nbsp;Контрагента, в&nbsp;том числе,
                    но&nbsp;не&nbsp;ограничиваясь, для подписания электронных документов и&nbsp;обмена ими через Личный
                    кабинет.
                  </ListItem>
                  <ListItem section="7.1.7">
                    Осуществлять просмотр модератором (работником КУБЕР) информации, размещаемой или планируемой
                    к&nbsp;размещению Контрагентом в&nbsp;Личном кабинете на&nbsp;предмет её&nbsp;соответствия
                    положениям Правил и&nbsp;действующему законодательству&nbsp;РФ, а&nbsp;также исправление или
                    удаление такой информации с&nbsp;Сайта.
                  </ListItem>
                  <ListItem section="7.1.8">
                    В&nbsp;любой момент по&nbsp;своему усмотрению&nbsp;и (или) при возникновении необходимости провести
                    процедуру Идентификации пользователя, запросив для этих целей необходимые подтверждающие документы.
                  </ListItem>
                  <ListItem section="7.1.9">
                    Изменять оформление, структуру и&nbsp;Содержание Сайта в&nbsp;любое время по&nbsp;своему усмотрению.
                  </ListItem>
                  <ListItem section="7.1.10" doubleDigit={true}>
                    Самостоятельно изменять и&nbsp;дополнять Правила без согласования с Контрагентом, уведомив его
                    об&nbsp;этом посредством размещения соответствующей информации на&nbsp;Сайте. Новая редакция Правил
                    вступает в&nbsp;силу с&nbsp;момента их&nbsp;опубликования на&nbsp;Сайте, если иной срок вступления
                    изменений&nbsp;и (или) дополнений в&nbsp;силу не&nbsp;определен КУБЕР.
                  </ListItem>
                  <ListItem section="7.1.11" doubleDigit={true}>
                    Прекращать действие Правил в&nbsp;одностороннем порядке, уведомив об&nbsp;этом Контрагента
                    посредством размещения соответствующей информации на&nbsp;Сайте.
                  </ListItem>
                </ol>
              </ol>
            </div>
            <div className="flex flex-col gap-4" id="responsibility">
              <h2 className="text-title1">8. Ответственность сторон</h2>
              <ol className="flex flex-col gap-4">
                <ListItem semibold={true} className="!gap-3" section="8.1.">
                  Контрагент несет ответственность:
                </ListItem>
                <ol className="flex flex-col gap-4 pb-4 pl-10">
                  <ListItem section="8.1.1.">
                    За полноту и достоверность всей информации и документов, размещаемых им в Личном кабинете.
                  </ListItem>
                  <ListItem section="8.1.2.">
                    За Юридически значимые действия, совершаемые в том числе, но не ограничиваясь, любыми третьими
                    лицами в Личном кабинете от лица Контрагента.
                  </ListItem>
                  <ListItem section="8.1.3.">
                    За намеренное или непреднамеренное предоставление доступа к Аккаунту третьим лицам, в том числе, но
                    не ограничиваясь, получившим такой доступ в результате компрометации Логина и (или) Пароля и (или)
                    смс-кода подтверждения. В указанных случаях права и обязанности, порожденные использованием
                    Аккаунта, возникают непосредственно у Контрагента, а ответственность за любые действия третьих лиц
                    несет Контрагент.
                  </ListItem>
                  <ListItem section="8.1.4.">
                    За самостоятельный контроль изменений и (или) дополнений, вносимых в Правила, а также за все
                    возможные последствия, вызванные несоблюдением данного требования.
                  </ListItem>
                </ol>
                <ListItem section="8.2." semibold={true} className="!gap-3">
                  КУБЕР не несет ответственность:
                </ListItem>
                <ol className="flex flex-col gap-4 pb-4 pl-10">
                  <ListItem section="8.2.1.">
                    За любые сбои, перерывы и неисправности в функционировании Сайта, вызванные внешними факторами, в
                    том числе, но не ограничиваясь, наличием и состоянием Технических средств и Технических ресурсов, а
                    также обусловленные ими потери информации у Контрагента. При этом КУБЕР гарантирует предпринимать
                    все возможные меры для обеспечения функционирования Сайта.
                  </ListItem>
                  <ListItem section="8.2.2.">
                    За соответствие функционала Сайта конкретным целям и ожиданиям Посетителей, Пользователей,
                    Контрагентов, и за специфические результаты от его использования, вызванные такими целями и
                    ожиданиями.
                  </ListItem>
                  <ListItem section="8.2.3.">
                    За любой моральный и материальный вред, предусмотренный действующим законодательством РФ,
                    причиненный Контрагенту и иным лицам в связи с использованием Сайта.
                  </ListItem>
                  <ListItem section="8.2.4.">
                    За размещение Контрагентом в Личном кабинете любой информации, которая прямо или косвенно может
                    причинить материальный и моральный вред любым третьим лицам.
                  </ListItem>
                  <ListItem section="8.2.5.">
                    За разглашение персональных данных Пользователей, произошедшее не по вине КУБЕР, в том числе, но не
                    ограничиваясь, в связи с действиями (бездействием) самих Пользователей, а также действиями третьих
                    лиц.
                  </ListItem>
                  <ListItem section="8.2.6.">
                    За иные неправомерные действия, исходящие от установленных и неустановленных лиц и источников,
                    включая, но не ограничиваясь, вредоносное программное обеспечение (вирусы, черви, троянские и
                    шпионские программы), ботнеты (вредоносные боты), DoS- и DDoS-атаки (отказ в обслуживании), а также
                    за последствия таких действий.
                  </ListItem>
                  <ListItem section="8.2.7.">
                    За форс-мажорные и иные обстоятельства, независящие от КУБЕР, а также за последствия таких
                    обстоятельств.
                  </ListItem>
                  <ListItem section="8.2.8.">
                    За содержание сайтов третьих лиц и действия Посетителей и Пользователей на сайтах третьих лиц, на
                    которые они могут перейти с Сайта по внешним веб-ссылкам.
                  </ListItem>
                </ol>
              </ol>
            </div>
            <div className="flex flex-col gap-4 pb-4" id="security">
              <h2 className="text-title1">9. Безопасность</h2>
              <ol className="flex flex-col gap-4">
                <ListItem section="9.1.">
                  КУБЕР осуществляет обработку персональных данных физических лиц на основании Политики обработки и
                  защиты персональных данных ООО «КУБЕР», предоставленных согласий на обработку персональных данных, и
                  действующего законодательства РФ.
                </ListItem>
                <ListItem section="9.2.">
                  Вся информация о деятельности КУБЕР и договорных правоотношениях, стороной которых является КУБЕР,
                  считается конфиденциальной, за исключением той, которая в соответствии с действующим законодательством
                  РФ не может быть отнесена к коммерческой тайне и конфиденциальной информации. Контрагент не вправе
                  передавать конкурентам КУБЕР любые сведения о деятельности КУБЕР, включая Договор, коммерческие
                  предложения КУБЕР, деловую переписку между сторонами с участием КУБЕР и иные сведения, относящиеся к
                  правоотношениям, участником которых является КУБЕР. Положения настоящего пункта Правил имеют силу в
                  течение всего периода действия Договора, и в течение 3 (трёх) лет с момента прекращения его действия,
                  если в вышеуказанный период КУБЕР не сообщит Контрагенту в письменной форме об ином сроке.
                </ListItem>
              </ol>
            </div>
            <div className="flex flex-col gap-4 pb-4" id="super-rights">
              <h2 className="text-title1">10. Исключительные права</h2>
              <ol className="flex flex-col gap-4">
                <ListItem section="10.1." doubleDigit={true}>
                  Все элементы Содержания сайта являются объектами исключительных прав КУБЕР, все права на эти объекты
                  защищены.
                </ListItem>
                <ListItem section="10.2." doubleDigit={true}>
                  Содержание сайта, программный код Сайта, доменные имена, фирменное наименование, товарный знак,
                  фирменный стиль являются интеллектуальной собственностью КУБЕР и охраняются в соответствии с
                  действующим законодательством РФ. Никто не имеет права воспроизводить, копировать, модифицировать,
                  рассылать, передавать, скачивать, публиковать, транслировать, распространять любым другим способом,
                  или использовать указанную интеллектуальную собственность КУБЕР иным образом целиком или по частям.
                </ListItem>
              </ol>
            </div>
            <div className="flex flex-col gap-4 pb-4" id="other">
              <h2 className="text-title1">11. Прочие условия</h2>
              <ol className="flex flex-col gap-4">
                <ListItem section="11.1." doubleDigit={true}>
                  Правила разработаны в соответствии с требованиями действующего законодательства РФ и во исполнение
                  таких требований, распространяются на отношения, связанные с правами и интересами третьих лиц, не
                  являющимися Контрагентами и (или) Пользователями, но чьи права и интересы могут быть затронуты в
                  результате действий Контрагентов и (или) Пользователей.
                </ListItem>
                <ListItem section="11.2." doubleDigit={true}>
                  К Правилам относятся вся информация и документы, не указанные в Правилах, но являющиеся неотъемлемой
                  частью настоящих Правил и регламентирующие отдельные действия Контрагентов и (или) Пользователей.
                </ListItem>
                <ListItem section="11.3." doubleDigit={true}>
                  Если по любым основаниям одно или несколько положений Правил будут признаны не имеющими юридической
                  силы, это не отменяет действие остальных положений настоящих Правил.
                </ListItem>
                <ListItem section="11.4." doubleDigit={true}>
                  Правила вступают в силу с момента первой публикации на Сайте и действуют бессрочно.
                </ListItem>
                <ListItem section="11.5." doubleDigit={true}>
                  Правила регулируются и толкуются в соответствии с действующим законодательством РФ. Вопросы, не
                  урегулированные настоящими Правилами, подлежат разрешению в соответствии с действующим
                  законодательством РФ.
                </ListItem>
                <ListItem section="11.6." doubleDigit={true}>
                  В случае возникновения любых споров или разногласий, связанных с исполнением Правил, Контрагент и
                  КУБЕР приложат все усилия для их разрешения путем проведения переговоров. В случае, если споры не
                  будут разрешены путем переговоров, они подлежат разрешению в порядке, установленном действующим
                  законодательством РФ.
                </ListItem>
                <ListItem section="11.7." doubleDigit={true}>
                  Правила составлены на русском языке и могут быть предоставлены для ознакомления на ином языке. В
                  случае расхождения русскоязычной версии Правил и их версии на ином языке, применяются положения
                  русскоязычной версии Правил.
                </ListItem>
                <ListItem section="11.8." doubleDigit={true}>
                  С целью получения информации по вопросам использования Сайта предусмотрена форма обратной связи,
                  размещенная на Сайте. Данная форма может быть использована в том числе, но не ограничиваясь, для
                  направления отзывов и жалоб, связанных с функционированием Сайта.
                </ListItem>
              </ol>
            </div>
          </div>
          <div className={clsx("relative hidden h-[488px] w-[397px] pl-[52px] desktop:flex desktop:basis-1/3")}>
            <div
              className={clsx("fixed flex flex-col gap-2 transition-all", {
                "top-[282px]": scrollY >= 0 && scrollY < scrollYAfterHeader,
                "top-[120px]": scrollY > 0,
                "opacity-0": scrollY > scrollYBeforeFooter,
              })}
            >
              <h3 className="text-textMediumSemi">Оглавление</h3>
              <p>
                1.&nbsp;&nbsp;&nbsp;
                <Link href="#definitions">
                  <span className="cursor-pointer underline underline-offset-[3px]">Понятия и определения</span>
                </Link>
              </p>
              <p>
                2.&nbsp;&nbsp;&nbsp;
                <Link href="#registration">
                  <span className="cursor-pointer underline underline-offset-[3px]">Регистрация и аутентификация</span>
                </Link>
              </p>
              <p>
                3.&nbsp;&nbsp;&nbsp;
                <Link href="#authorization">
                  <span className="cursor-pointer underline underline-offset-[3px]">Авторизация</span>
                </Link>
              </p>
              <p>
                4.&nbsp;&nbsp;&nbsp;
                <Link href="#verification">
                  <span className="cursor-pointer underline underline-offset-[3px]">Верификация аккаунта</span>
                </Link>
              </p>
              <p>
                5.&nbsp;&nbsp;&nbsp;
                <Link href="#cabinet">
                  <span className="cursor-pointer underline underline-offset-[3px]">Личный кабинет</span>
                </Link>
              </p>
              <p>
                6.&nbsp;&nbsp;&nbsp;
                <Link href="#agent-rights">
                  <span className="cursor-pointer underline underline-offset-[3px]">
                    Права и обязанности контрагента
                  </span>
                </Link>
              </p>
              <p>
                7.&nbsp;&nbsp;&nbsp;
                <Link href="#kuber-rights">
                  <span className="cursor-pointer underline underline-offset-[3px]">Права и обязанности КУБЕР</span>
                </Link>
              </p>
              <p>
                8.&nbsp;&nbsp;&nbsp;
                <Link href="#responsibility">
                  <span className="cursor-pointer underline underline-offset-[3px]">Ответственность сторон</span>
                </Link>
              </p>
              <p>
                9.&nbsp;&nbsp;&nbsp;
                <Link href="#security">
                  <span className="cursor-pointer underline underline-offset-[3px]">Безопасность</span>
                </Link>
              </p>
              <p>
                10.&nbsp;
                <Link href="#super-rights">
                  <span className="cursor-pointer underline underline-offset-[3px]">Исключительные права</span>
                </Link>
              </p>
              <p>
                11.&nbsp;
                <Link href="#other">
                  <span className="cursor-pointer underline underline-offset-[3px]">Прочие условия</span>
                </Link>
              </p>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
};

export default PrivacyPolicyPage;

const ListItem: React.FC<
  HTMLAttributes<HTMLLIElement> & {
  section: string;
  semibold?: boolean;
  doubleDigit?: boolean;
}
> = ({ className, section, semibold, doubleDigit, children, ...props }) => (
  <li
    className={clsx("flex flex-row gap-5", className, {
      "font-semibold": semibold,
      "!gap-3": doubleDigit,
    })}
    {...props}
  >
    <span>{section}</span>
    <span>{children}</span>
  </li>
);
